// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Cargas.tsx
//  Listagem de Cargas / Vendas Externas por Rota. Busca, status, período,
//  veículo, rota, ordenação e KPIs rodam NO SERVIDOR; os filtros ficam na URL.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Truck, PackageCheck, CheckCircle2,
  Eye, Pencil, MapPin, RotateCcw, Loader2,
} from 'lucide-react';
import { useCarga } from '@/features/operacional/hooks/useCarga';
import { calcularStatusCarga } from '@/features/operacional/types';
import type { StatusCarga } from '@/features/operacional/types';
import type { CargaListagem } from '@/features/operacional/services/cargaService';
import { useRotaCombo } from '@/features/rotas/hooks/useRotas';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarraFiltros } from '@/components/BarraFiltros';
import { BotaoExportar } from '@/components/BotaoExportar';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { VeiculoSelect } from '@/components/VeiculoSelect';
import { CondutorSelect } from '@/components/CondutorSelect';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';

const statusConfig: Record<StatusCarga, { label: string; className: string; icon: typeof Truck }> = {
  montagem:   { label: 'Montagem',  className: 'bg-blue-500/10 text-blue-500',    icon: PackageCheck },
  em_rota:    { label: 'Em Rota',   className: 'bg-orange-500/10 text-orange-500', icon: Truck        },
  retornada:  { label: 'Retornada', className: 'bg-purple-500/10 text-purple-500', icon: RotateCcw    },
  finalizada: { label: 'Finalizada',className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
};

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const data = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : null);
const vazio = <span className="text-muted-foreground">—</span>;

const NENHUMA = '__none__';

type Campo = 'numero' | 'motorista' | 'montagem' | 'valor';

const PADROES = {
  busca: '',
  status: 'todos',
  dtInicio: '',
  dtFim: '',
  veiculoId: 0,
  rotaId: 0,
  motorista: '',
  ordenarPor: 'montagem',
  direcao: 'desc',
  pagina: 1,
  tamanhoPagina: 20,
};

const Cargas = () => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { rotas } = useRotaCombo();

  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });

  const { cargas, total, totalPaginas, isLoading, isFetching, deleteCarga, resumo } = useCarga({
    pagina: pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
    filtros: {
      busca: filtros.busca, status: filtros.status, dtInicio: filtros.dtInicio, dtFim: filtros.dtFim,
      veiculoId: filtros.veiculoId, rotaId: filtros.rotaId, motorista: filtros.motorista,
      ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao,
    },
  });

  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const onOrdenar = (campo: Campo) =>
    setFiltros(alternarOrdenacao(ordenacao, campo, campo === 'motorista' ? 'asc' : 'desc'));
  const cabecalho = { ...ordenacao, onOrdenar };
  const kpi = (v?: number) => (resumo ? v ?? 0 : '…');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Cargas / Rota</h1>
          <p className="text-muted-foreground mt-1">Gerencie as vendas externas por rota</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/cargas/nova')}>
          <Plus className="h-4 w-4" />Nova Carga
        </Button>
      </div>

      {/* KPIs — calculados no servidor sobre o filtro (todas as páginas) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Em Montagem
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{kpi(resumo?.montagem)}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando saída</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Truck className="h-4 w-4" />Em Rota
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{kpi(resumo?.emRota)}</div>
            <p className="text-xs text-muted-foreground mt-1">Em campo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />Retornadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-500">{kpi(resumo?.retornadas)}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando conferência</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Finalizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{kpi(resumo?.finalizadas)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {kpi(resumo?.total)} {filtrosAtivos > 0 ? 'cargas no filtro' : 'cargas'} · {BRL(resumo?.valorTotal ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº da carga ou motorista..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
        acoes={<BotaoExportar endpoint="/api/cargas/exportar" params={filtros} />}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="montagem">Em Montagem</SelectItem>
              <SelectItem value="em_rota">Em Rota</SelectItem>
              <SelectItem value="retornada">Retornada</SelectItem>
              <SelectItem value="finalizada">Finalizada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Montagem"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        <div className="space-y-1 w-56">
          <Label className="text-xs text-muted-foreground">Motorista</Label>
          <CondutorSelect
            value={filtros.motorista}
            onChange={(motorista) => setFiltros({ motorista })}
            placeholder="Todos os motoristas"
          />
        </div>
        <div className="space-y-1 w-52">
          <Label className="text-xs text-muted-foreground">Veículo</Label>
          <VeiculoSelect
            value={filtros.veiculoId || undefined}
            onChange={(id) => setFiltros({ veiculoId: id ?? 0 })}
            placeholder="Todos os veículos"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Rota</Label>
          <Select
            value={filtros.rotaId ? String(filtros.rotaId) : NENHUMA}
            onValueChange={(v) => setFiltros({ rotaId: v === NENHUMA ? 0 : Number(v) })}
          >
            <SelectTrigger className="h-9 w-[180px]" aria-label="Rota"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NENHUMA}>Todas as rotas</SelectItem>
              {rotas.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>{r.descricao}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead campo="numero" {...cabecalho} className="w-20">Nº</SortableTableHead>
                  <SortableTableHead campo="motorista" {...cabecalho}>Motorista</SortableTableHead>
                  <TableHead>Veículo</TableHead>
                  <TableHead>Rota</TableHead>
                  <SortableTableHead campo="montagem" {...cabecalho}>Montagem</SortableTableHead>
                  <TableHead>Saída</TableHead>
                  <TableHead>Retorno</TableHead>
                  <SortableTableHead campo="valor" {...cabecalho} alinharDireita>Valor Total</SortableTableHead>
                  <TableHead className="text-right">Divergência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : cargas.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    {filtros.dtInicio || filtros.dtFim
                      ? 'Nenhuma carga neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma carga encontrada'}
                  </TableCell></TableRow>
                ) : (
                  cargas.map((c: CargaListagem) => {
                    const status = calcularStatusCarga(c);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeEditar = !c.isFinalizado;
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.numeroCarga}</TableCell>
                        <TableCell>{c.motorista || vazio}</TableCell>
                        <TableCell className="whitespace-nowrap">{c.veiculoDescricao || vazio}</TableCell>
                        <TableCell>
                          {c.rotaDescricao
                            ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{c.rotaDescricao}</span>
                            : vazio}
                        </TableCell>
                        <TableCell>{data(c.dtMontagem)}</TableCell>
                        <TableCell>{data(c.dtSaida) ?? vazio}</TableCell>
                        <TableCell>{data(c.dtRetorno) ?? vazio}</TableCell>
                        <TableCell className="text-right font-medium">{BRL(c.valorTotalCarga ?? 0)}</TableCell>
                        <TableCell className="text-right">
                          {c.valorDivergencia !== 0
                            ? <span className="text-destructive font-medium">{BRL(c.valorDivergencia)}</span>
                            : vazio}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeEditar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/cargas/${c.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/cargas/${c.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {podeEditar && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(c.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteCarga(deletingId); setDeletingId(null); } }}
        title="Excluir Carga"
        description="Tem certeza que deseja excluir esta carga?"
      />
    </div>
  );
};

export default Cargas;
